/* @img-migrated */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight, MessageSquareQuote } from 'lucide-react';

import Img from './Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTOPLAY_MS = 6000;

/**
 * TestimonialsCarousel — premium autoplay carousel for member quotes.
 * - Soft fade + subtle x-shift transition
 * - Mobile swipe via Framer Motion drag
 * - Pauses on hover (desktop) and on touch
 * - Loops continuously
 */
export const TestimonialsCarousel = () => {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/testimonials?limit=10`);
        setItems(res.data.items || []);
      } catch { /* */ }
    };
    load();
  }, []);

  const goNext = useCallback(() => {
    setDirection(1);
    setIndex((i) => (items.length ? (i + 1) % items.length : 0));
  }, [items.length]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setIndex((i) => (items.length ? (i - 1 + items.length) % items.length : 0));
  }, [items.length]);

  useEffect(() => {
    if (paused || items.length < 2) return;
    timerRef.current = setTimeout(goNext, AUTOPLAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [index, paused, goNext, items.length]);

  if (!items.length) return null;
  const t = items[index];

  return (
    <section className="py-14 sm:py-20 px-5 sm:px-8 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden" data-testid="testimonials-carousel">
      <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-amber-100/30 blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-[#002249]/5 blur-3xl" />
      <div className="relative max-w-3xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <span className="inline-flex items-center gap-1.5 text-[10.5px] sm:text-xs font-bold text-[#E63946] tracking-[0.18em] uppercase mb-3">
            <MessageSquareQuote className="w-3.5 h-3.5" /> Members
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#002249] tracking-tight leading-tight">What Our Members Are Saying</h2>
        </motion.div>

        <div
          className="relative mt-10 sm:mt-12 min-h-[260px] sm:min-h-[230px]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
        >
          <AnimatePresence custom={direction} mode="wait">
            <motion.article
              key={t.id}
              custom={direction}
              initial={{ opacity: 0, x: direction * 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -24 }}
              transition={{ duration: 0.5, ease: [0.2, 0, 0, 1] }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(_, info) => {
                if (info.offset.x < -60) goNext();
                else if (info.offset.x > 60) goPrev();
              }}
              className="bg-white rounded-2xl border border-slate-200 shadow-[0_10px_40px_-12px_rgba(0,34,73,0.12)] p-7 sm:p-10 cursor-grab active:cursor-grabbing"
              data-testid="testimonial-card"
            >
              <Quote className="w-7 h-7 text-[#E63946] mx-auto mb-4 opacity-90" />
              <p className="text-[16px] sm:text-[18px] md:text-[20px] font-medium text-[#1e293b] leading-relaxed">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="mt-7 flex items-center justify-center gap-3">
                {t.photo_url ? (
                  <Img variant="generic" src={resolveImg(t.photo_url)} alt={t.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#002249] to-[#0a3570] flex items-center justify-center text-white font-bold text-[15px] shadow-sm">
                    {t.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="text-left">
                  <p className="text-[14px] font-bold text-[#002249] leading-tight">{t.name}</p>
                  {(t.role || t.company) && (
                    <p className="text-[12px] text-slate-500 mt-0.5">
                      {t.role}{t.role && t.company ? ' · ' : ''}{t.company}
                    </p>
                  )}
                </div>
              </div>
            </motion.article>
          </AnimatePresence>
        </div>

        {/* Controls + Dots */}
        {items.length > 1 && (
          <div className="flex items-center justify-center gap-3 sm:gap-4 mt-8">
            <button
              onClick={goPrev}
              className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-500 hover:text-[#002249] hover:border-[#002249] flex items-center justify-center transition-colors shadow-sm"
              aria-label="Previous testimonial"
              data-testid="testimonial-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-1.5">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setDirection(i > index ? 1 : -1); setIndex(i); }}
                  className={`transition-all ${i === index ? 'w-6 h-2 bg-[#002249] rounded-full' : 'w-2 h-2 rounded-full bg-slate-300 hover:bg-slate-400'}`}
                  aria-label={`Go to testimonial ${i + 1}`}
                  data-testid={`testimonial-dot-${i}`}
                />
              ))}
            </div>
            <button
              onClick={goNext}
              className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-500 hover:text-[#002249] hover:border-[#002249] flex items-center justify-center transition-colors shadow-sm"
              aria-label="Next testimonial"
              data-testid="testimonial-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
